import * as THREE from "three";
import {
  LOUNGE_CHAIR_2_CENTER_BACK_SVG_OUTLINE,
  LOUNGE_CHAIR_2_LEFT_ARM_SVG_OUTLINE,
  LOUNGE_CHAIR_2_RIGHT_ARM_SVG_OUTLINE,
  LOUNGE_CHAIR_2_SVG_BOUNDS,
} from "./loungeChair2SvgOutlines";

export function getLoungeChair2Radii(
  width: number,
  depth: number,
  outerRadiusRatio = 0.47,
  wallThicknessRatio = 0.09,
): { outerRadius: number; innerRadius: number } {
  const outerRadius = Math.min(width, depth) * outerRadiusRatio;
  const innerRadius = Math.max(
    outerRadius - Math.min(width, depth) * wallThicknessRatio,
    outerRadius * 0.55,
  );
  return { outerRadius, innerRadius };
}

/** Samples a circular arc into a polyline. */
function createArcOutline(
  centerX: number,
  centerY: number,
  radius: number,
  startAngle: number,
  endAngle: number,
  segments: number,
  clockwise: boolean,
): [number, number][] {
  const points: [number, number][] = [];
  const normalizedEnd =
    endAngle < startAngle && !clockwise ? endAngle + Math.PI * 2 : endAngle;
  const normalizedStart =
    startAngle < endAngle && clockwise ? startAngle + Math.PI * 2 : startAngle;
  const span = clockwise
    ? normalizedStart - normalizedEnd
    : normalizedEnd - startAngle;

  for (let index = 0; index <= segments; index += 1) {
    const t = index / segments;
    const angle = clockwise ? startAngle - span * t : startAngle + span * t;
    points.push([
      centerX + Math.cos(angle) * radius,
      centerY + Math.sin(angle) * radius,
    ]);
  }

  return points;
}

/** Rotates footprint points 180° to align with the 2D symbol orientation. */
function rotateFootprint180(points: [number, number][]): [number, number][] {
  return points.map(([x, y]) => [-x, -y]);
}

/** Builds a single seat cylinder that runs from the floor up inside the wrapper. */
export function createLoungeChair2SeatOutline(
  width: number,
  depth: number,
  innerInsetRatio = 0.94,
  segments = 48,
): [number, number][] {
  const { innerRadius } = getLoungeChair2Radii(width, depth);
  const seatRadius = innerRadius * innerInsetRatio;
  return rotateFootprint180(
    createArcOutline(0, 0, seatRadius, 0, Math.PI * 2, segments, false),
  );
}

const pointsEqual = (left: [number, number], right: [number, number]) =>
  left[0] === right[0] && left[1] === right[1];

function sliceOutline(
  points: [number, number][],
  start: [number, number],
  end: [number, number],
): [number, number][] {
  const startIndex = points.findIndex((point) => pointsEqual(point, start));
  const endIndex = points.findIndex(
    (point, index) => index >= startIndex && pointsEqual(point, end),
  );
  if (startIndex < 0 || endIndex < startIndex) {
    throw new Error("Unable to resolve Lounge Chair 2 reference outline");
  }
  return points.slice(startIndex, endIndex + 1);
}

function appendWithoutDuplicate(
  target: [number, number][],
  points: [number, number][],
) {
  points.forEach((point) => {
    if (!target.length || !pointsEqual(target[target.length - 1], point)) {
      target.push(point);
    }
  });
}

/**
 * Builds the broad, shallow U-shaped backrest traced from the reference symbol.
 * The outer and inner paths deliberately retain the flatter rear span and the
 * tapered forward ends instead of approximating the chair with a circle.
 */
export function createLoungeChair2WrapperOutline(
  width: number,
  depth: number,
): [number, number][] {
  const { outer, inner } = createLoungeChair2WrapperPaths(width, depth);
  return [...outer, ...inner.slice().reverse()];
}

function createLoungeChair2WrapperPaths(
  width: number,
  depth: number,
): { outer: [number, number][]; inner: [number, number][] } {
  const outerLeft = sliceOutline(
    LOUNGE_CHAIR_2_LEFT_ARM_SVG_OUTLINE,
    [22.11, 96.12],
    [41.7, 32.82],
  );
  const outerBack = sliceOutline(
    LOUNGE_CHAIR_2_CENTER_BACK_SVG_OUTLINE,
    [103.35, 32.79],
    [41.7, 32.82],
  ).reverse();
  const outerRight = sliceOutline(
    LOUNGE_CHAIR_2_RIGHT_ARM_SVG_OUTLINE,
    [103.35, 32.79],
    [122.85, 94.62],
  );
  const innerRight = sliceOutline(
    LOUNGE_CHAIR_2_RIGHT_ARM_SVG_OUTLINE,
    [122.85, 94.62],
    [91.35, 64.38],
  ).reverse();
  const innerBack = sliceOutline(
    LOUNGE_CHAIR_2_CENTER_BACK_SVG_OUTLINE,
    [53.64, 64.29],
    [91.35, 64.41],
  );
  const innerLeft = sliceOutline(
    LOUNGE_CHAIR_2_LEFT_ARM_SVG_OUTLINE,
    [53.64, 64.29],
    [21.99, 94.62],
  ).reverse();
  const outerSvg: [number, number][] = [];
  const innerSvg: [number, number][] = [];
  [outerLeft, outerBack, outerRight].forEach((path) =>
    appendWithoutDuplicate(outerSvg, path),
  );
  [innerLeft, innerBack, innerRight].forEach((path) =>
    appendWithoutDuplicate(innerSvg, path),
  );

  const centerX =
    (LOUNGE_CHAIR_2_SVG_BOUNDS.minX + LOUNGE_CHAIR_2_SVG_BOUNDS.maxX) /
    2;
  const centerY =
    (LOUNGE_CHAIR_2_SVG_BOUNDS.minY + LOUNGE_CHAIR_2_SVG_BOUNDS.maxY) /
    2;
  const svgWidth =
    LOUNGE_CHAIR_2_SVG_BOUNDS.maxX - LOUNGE_CHAIR_2_SVG_BOUNDS.minX;
  const svgDepth =
    LOUNGE_CHAIR_2_SVG_BOUNDS.maxY - LOUNGE_CHAIR_2_SVG_BOUNDS.minY;
  const bevelAllowance = 0.05;
  const scaleX = Math.max(width - bevelAllowance, width * 0.8) / svgWidth;
  const scaleY = Math.max(depth - bevelAllowance, depth * 0.8) / svgDepth;

  const transform = ([x, y]: [number, number]): [number, number] => [
    (x - centerX) * scaleX,
    -(y - centerY) * scaleY,
  ];

  return {
    outer: outerSvg.map(transform),
    inner: innerSvg.map(transform),
  };
}

export type LoungeChair2ShellGeometryOptions = {
  width: number;
  depth: number;
  height: number;
  armHeight: number;
  pathSegments?: number;
  railSegments?: number;
};

type LoungeChair2PathSample = {
  x: number;
  y: number;
  normalX: number;
  normalY: number;
  halfThickness: number;
  crownHeight: number;
  backProximity: number;
};

function smoothstep01(value: number): number {
  const clamped = THREE.MathUtils.clamp(value, 0, 1);
  return clamped * clamped * (3 - 2 * clamped);
}

function resampleOpenPath(
  points: [number, number][],
  count: number,
): [number, number][] {
  const distances = [0];
  for (let index = 1; index < points.length; index += 1) {
    distances.push(
      distances[index - 1] +
        Math.hypot(
          points[index][0] - points[index - 1][0],
          points[index][1] - points[index - 1][1],
        ),
    );
  }
  const totalLength = distances[distances.length - 1];
  let segmentIndex = 1;

  return Array.from({ length: count }, (_, sampleIndex) => {
    const targetDistance =
      (sampleIndex / Math.max(count - 1, 1)) * totalLength;
    while (
      segmentIndex < distances.length - 1 &&
      distances[segmentIndex] < targetDistance
    ) {
      segmentIndex += 1;
    }
    const startDistance = distances[segmentIndex - 1];
    const segmentLength = Math.max(
      distances[segmentIndex] - startDistance,
      1e-6,
    );
    const progress = (targetDistance - startDistance) / segmentLength;
    return [
      THREE.MathUtils.lerp(
        points[segmentIndex - 1][0],
        points[segmentIndex][0],
        progress,
      ),
      THREE.MathUtils.lerp(
        points[segmentIndex - 1][1],
        points[segmentIndex][1],
        progress,
      ),
    ];
  });
}

/**
 * Builds the second lounge chair's broad reference backrest as a swept shell.
 * The lower rings pull inward behind the cushion while the upper rings flare
 * to the exact traced 2D outline. This preserves the characteristic side
 * waist without introducing a triangulated, circular-looking top cap.
 */
export function createLoungeChair2ShellBufferGeometry({
  width,
  depth,
  height,
  armHeight,
  pathSegments = 72,
  railSegments = 10,
}: LoungeChair2ShellGeometryOptions): THREE.BufferGeometry {
  const safeHeight = Math.max(height, 0.08);
  const safeArmHeight = THREE.MathUtils.clamp(
    armHeight,
    safeHeight * 0.28,
    safeHeight - 0.02,
  );
  const { outer, inner } = createLoungeChair2WrapperPaths(width, depth);
  const sampleCount = Math.max(pathSegments, 24);
  const outerSamples = resampleOpenPath(outer, sampleCount);
  const innerSamples = resampleOpenPath(inner, sampleCount);
  // Register the shell against the body's rear edge. The thin, uniform shell
  // otherwise sits far enough forward for the body to peek out behind it.
  const backOffset = depth * 0.075;
  const uniformHalfThickness = Math.min(width, depth) * 0.055;
  const centers = outerSamples.map(([outerX, outerY], index) => {
    const pathProgress = index / Math.max(sampleCount - 1, 1);
    const backProximity = 1 - Math.abs(pathProgress * 2 - 1);
    const forwardEndRetreat =
      depth * 0.055 * (1 - smoothstep01(backProximity / 0.58));
    return [
      (outerX + innerSamples[index][0]) / 2,
      (outerY + innerSamples[index][1]) / 2 +
        backOffset +
        forwardEndRetreat,
    ] as [number, number];
  });
  const path: LoungeChair2PathSample[] = centers.map(([x, y], index) => {
    const outerPoint = outerSamples[index];
    const innerPoint = innerSamples[index];
    const wallX = outerPoint[0] - innerPoint[0];
    const wallY = outerPoint[1] - innerPoint[1];
    const wallThickness = Math.max(Math.hypot(wallX, wallY), 0.004);
    const pathProgress = index / Math.max(sampleCount - 1, 1);
    const backProximity = 1 - Math.abs(pathProgress * 2 - 1);
    const crownRise = smoothstep01((backProximity - 0.035) / 0.38);

    return {
      x,
      y,
      normalX: wallX / wallThickness,
      normalY: wallY / wallThickness,
      halfThickness: uniformHalfThickness,
      crownHeight: THREE.MathUtils.lerp(
        safeArmHeight,
        safeHeight,
        crownRise,
      ),
      backProximity,
    };
  });

  const railRise = Math.min(Math.min(width, depth) * 0.035, 0.028);
  const crossSections = path.map((sample) => {
    const sideTop = Math.max(sample.crownHeight - railRise, 0.04);
    const section: Array<[number, number]> = [
      [1, 0],
      [1, sideTop * 0.1],
      [1, sideTop * 0.2],
      [1, sideTop * 0.3],
      [1, sideTop * 0.4],
      [1, sideTop * 0.5],
      [1, sideTop * 0.58],
      [1, sideTop * 0.64],
      [1, sideTop * 0.7],
      [1, sideTop * 0.76],
      [1, sideTop * 0.82],
      [1, sideTop * 0.88],
      [1, sideTop * 0.94],
      [1, sideTop],
    ];
    for (let index = 1; index < railSegments; index += 1) {
      const angle = (Math.PI * index) / railSegments;
      section.push([
        Math.cos(angle),
        sideTop + railRise * Math.sin(angle),
      ]);
    }
    section.push(
      [-1, sideTop],
      [-1, sideTop * 0.94],
      [-1, sideTop * 0.88],
      [-1, sideTop * 0.82],
      [-1, sideTop * 0.76],
      [-1, sideTop * 0.7],
      [-1, sideTop * 0.64],
      [-1, sideTop * 0.58],
      [-1, sideTop * 0.5],
      [-1, sideTop * 0.4],
      [-1, sideTop * 0.3],
      [-1, sideTop * 0.2],
      [-1, sideTop * 0.1],
      [-1, 0],
    );
    return section;
  });

  const vertices: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const sectionSize = crossSections[0].length;

  /** Arc-length U so fabric/stone albedo stretches evenly along the backrest. */
  const pathDistances: number[] = [0];
  for (let index = 1; index < path.length; index += 1) {
    pathDistances.push(
      pathDistances[index - 1] +
        Math.hypot(
          path[index].x - path[index - 1].x,
          path[index].y - path[index - 1].y,
        ),
    );
  }
  const totalPathLength = Math.max(
    pathDistances[pathDistances.length - 1],
    1e-6,
  );

  path.forEach((sample, pathIndex) => {
    const u = pathDistances[pathIndex] / totalPathLength;
    crossSections[pathIndex].forEach(([normalRatio, z], sectionIndex) => {
      const verticalRatio = z / Math.max(sample.crownHeight, 1e-6);
      const flare = smoothstep01((verticalRatio - 0.08) / 0.88);
      // Let the lower side returns follow the cushion's broad front corners.
      // The traced endpoints otherwise turn inward before the 88%-width seat,
      // making the cushion appear to pass through the backrest in plan view.
      // Fade this small widening out through the shoulder so the reference
      // crown and the symbol's overall bounds stay unchanged.
      const centreScale = THREE.MathUtils.lerp(1.04, 1, flare);
      const normalOffset = sample.halfThickness * normalRatio;
      // Keep the lower rear of the D-profile over the body instead of letting
      // the seat base protrude behind the backrest. This fades out before the
      // shoulder rail so the characteristic upper curve is unchanged.
      const lowerBodyRegistration =
        1 - smoothstep01((verticalRatio - 0.34) / 0.3);
      const lowerBackAlignment =
        depth *
        0.043 *
        smoothstep01(sample.backProximity) *
        lowerBodyRegistration;
      vertices.push(
        sample.x * centreScale + sample.normalX * normalOffset,
        sample.y * THREE.MathUtils.lerp(0.95, 1, flare) +
          sample.normalY * normalOffset +
          lowerBackAlignment,
        z,
      );
      uvs.push(u, sectionIndex / sectionSize);
    });
  });

  for (let pathIndex = 0; pathIndex < path.length - 1; pathIndex += 1) {
    for (let sectionIndex = 0; sectionIndex < sectionSize; sectionIndex += 1) {
      const nextSectionIndex = (sectionIndex + 1) % sectionSize;
      const current = pathIndex * sectionSize + sectionIndex;
      const currentNext = pathIndex * sectionSize + nextSectionIndex;
      const following = (pathIndex + 1) * sectionSize + sectionIndex;
      const followingNext =
        (pathIndex + 1) * sectionSize + nextSectionIndex;
      indices.push(current, following, currentNext);
      indices.push(currentNext, following, followingNext);
    }
  }

  const addEndCap = (pathIndex: number, reverse: boolean) => {
    const ringStart = pathIndex * sectionSize;
    const capOutline = crossSections[pathIndex].map(
      ([normalRatio, z]) =>
        new THREE.Vector2(normalRatio * path[pathIndex].halfThickness, z),
    );
    const capTriangles = THREE.ShapeUtils.triangulateShape(capOutline, []);
    capTriangles.forEach(([first, second, third]) => {
      if (reverse) {
        indices.push(
          ringStart + first,
          ringStart + third,
          ringStart + second,
        );
      } else {
        indices.push(
          ringStart + first,
          ringStart + second,
          ringStart + third,
        );
      }
    });
  };

  addEndCap(0, true);
  addEndCap(path.length - 1, false);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(vertices, 3),
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function buildShapeFromOutline(
  points: [number, number][],
  holes?: [number, number][][],
): THREE.Shape {
  const [firstPoint, ...remainingPoints] = points;
  const shape = new THREE.Shape();
  shape.moveTo(firstPoint[0], firstPoint[1]);
  remainingPoints.forEach(([x, y]) => shape.lineTo(x, y));
  shape.closePath();

  holes?.forEach((holePoints) => {
    const [firstHolePoint, ...remainingHolePoints] = holePoints;
    if (!firstHolePoint) return;
    const hole = new THREE.Path();
    hole.moveTo(firstHolePoint[0], firstHolePoint[1]);
    remainingHolePoints.forEach(([x, y]) => hole.lineTo(x, y));
    hole.closePath();
    shape.holes.push(hole);
  });

  return shape;
}

/** Creates an extruded buffer geometry from footprint outlines. */
export function createLoungeChair2ExtrudedGeometry(
  points: [number, number][],
  settings: THREE.ExtrudeGeometryOptions,
  holes?: [number, number][][],
): THREE.ExtrudeGeometry {
  return new THREE.ExtrudeGeometry(
    buildShapeFromOutline(points, holes),
    settings,
  );
}

/**
 * Tapers wrapper wall height and thickness toward the front (-Y) so armrests
 * merge smoothly into the seat.
 */
export function applyLoungeChair2WrapperTopSlope(
  geometry: THREE.BufferGeometry,
  extrudeDepth: number,
  maxDrop: number,
): void {
  const position = geometry.attributes.position;
  const frontHeightRatio = Math.max(
    (extrudeDepth - maxDrop) / extrudeDepth,
    0.42,
  );
  let maxAbsX = 0;
  let backY = -Infinity;
  let frontY = Infinity;
  for (let index = 0; index < position.count; index += 1) {
    maxAbsX = Math.max(maxAbsX, Math.abs(position.getX(index)));
    backY = Math.max(backY, position.getY(index));
    frontY = Math.min(frontY, position.getY(index));
  }
  const longitudinalSpan = Math.max(backY - frontY, 1e-6);

  for (let index = 0; index < position.count; index += 1) {
    const x = position.getX(index);
    const y = position.getY(index);
    let z = position.getZ(index);

    const lateralRatio = THREE.MathUtils.clamp(
      Math.abs(x) / Math.max(maxAbsX, 1e-6),
      0,
      1,
    );
    const sideActivation = THREE.MathUtils.smoothstep(
      lateralRatio,
      0.28,
      0.78,
    );
    const longitudinalRatio = THREE.MathUtils.clamp(
      (backY - y) / longitudinalSpan,
      0,
      1,
    );
    const forwardDrop =
      sideActivation *
      longitudinalRatio *
      longitudinalRatio *
      (3 - 2 * longitudinalRatio);
    const heightScale = THREE.MathUtils.lerp(
      1,
      frontHeightRatio,
      forwardDrop,
    );
    z *= heightScale;
    position.setZ(index, z);
  }

  geometry.computeVertexNormals();
}
